import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from 'shared/ui/icons/Icon';
import Tip from 'shared/ui/Tip/Tip';
import { HOME_PATH, NavGroup, groupOfPath, isUnder } from './navigation';

const VERSION = process.env.REACT_APP_VERSION;

/*
 * Los enlaces recargan la página (`reloadDocument`), como el menú anterior. Las
 * pantallas dan por hecho que el store de Redux arranca vacío: navegando sin
 * recargar, al volver a una pantalla se veían los datos de la visita anterior
 * (p. ej. la tabla de Conf. de tipos llena con el combo de concepto vacío).
 */

interface SidebarProps {
  groups: NavGroup[];
  collapsed: boolean;
}

/**
 * Barra lateral clara con píldoras. Colapsada muestra solo iconos, cada uno con
 * su nombre en `title` y `aria-label`.
 */
const Sidebar = ({ groups, collapsed }: SidebarProps) => {
  const { pathname } = useLocation();
  /*
   * Todo arranca contraído: solo se abre lo que la persona despliega, y el
   * grupo de la pantalla que se está viendo, que se añade en el efecto de
   * abajo. Basta con el estado del componente porque el armazón se monta una
   * sola vez y sobrevive a la navegación.
   */
  const [open, setOpen] = useState<Set<string>>(new Set());

  // Llegar a una pantalla -por el menú, por un acceso de Inicio o recargando-
  // despliega su grupo, para no perder de vista dónde se está.
  useEffect(() => {
    const current = groupOfPath(pathname, groups);
    if (current) {
      setOpen((prev) => (prev.has(current.id) ? prev : new Set(Array.from(prev).concat(current.id))));
    }
  }, [pathname, groups]);

  const toggleGroup = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /*
   * Contraída solo se ven iconos, así que cada uno necesita decir su nombre. El
   * `title` del navegador tarda en salir y no admite estilo: se usa el mismo
   * globo del resto de la aplicación, a la derecha para no taparse con el menú.
   */
  const nameWhenCollapsed = (label: string) =>
    collapsed ? { 'aria-label': label } : {};

  const conNombre = (id: string, label: string, contenido: React.ReactElement) =>
    collapsed ? (
      <Tip id={`nav-${id}`} label={label} placement="right" tipo="menu">
        {contenido}
      </Tip>
    ) : (
      contenido
    );

  const homeActive = isUnder(pathname, HOME_PATH);

  return (
    <nav className={`app-sidebar${collapsed ? ' is-collapsed' : ''}`} aria-label="Menú principal">
      <ul className="app-nav">
        <li>
          {conNombre(
            'inicio',
            'Inicio',
            <Link
              to={HOME_PATH}
              reloadDocument
              className={`app-nav__item app-nav__item--top${homeActive ? ' is-active' : ''}`}
              aria-current={homeActive ? 'page' : undefined}
              {...nameWhenCollapsed('Inicio')}
            >
              <Icon name="home" size={18} />
              <span className="app-nav__label">INICIO</span>
            </Link>
          )}
        </li>

        {groups.map((group) => {
          const isOpen = open.has(group.id);
          const hasCurrent = group.items.some((item) => isUnder(pathname, item.path));
          const listId = `${group.id}-items`;
          return (
            <li key={group.id} className="app-nav__group">
              {conNombre(
                group.id,
                group.label,
                <button
                  type="button"
                  className={`app-nav__group-btn${hasCurrent ? ' is-current' : ''}`}
                  aria-expanded={isOpen}
                  aria-controls={listId}
                  onClick={() => toggleGroup(group.id)}
                  {...nameWhenCollapsed(group.label)}
                >
                  <Icon name={group.icon} size={15} />
                  <span className="app-nav__label">{group.label}</span>
                  <Icon name="chevronDown" size={14} className="app-nav__caret" />
                </button>
              )}
              <ul id={listId} className="app-nav__items" hidden={!isOpen}>
                {group.items.map((item) => {
                  const active = isUnder(pathname, item.path);
                  return (
                    <li key={item.id}>
                      {conNombre(
                        item.id,
                        item.label,
                        <Link
                          to={item.path}
                          reloadDocument
                          className={`app-nav__item${active ? ' is-active' : ''}`}
                          aria-current={active ? 'page' : undefined}
                          {...nameWhenCollapsed(item.label)}
                        >
                          <Icon name={item.icon} size={15} />
                          <span className="app-nav__label">{item.label}</span>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>

      {/* La versión sale de package.json (ver los scripts `start` y `build`). */}
      {VERSION && <div className="app-sidebar__foot">Versión {VERSION}</div>}
    </nav>
  );
};

export default Sidebar;
